import { ArrowLeft, LogIn } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'

import { mensajeDeEntrada } from '../api/cuenta'
import { pedir } from '../api/nucleo'
import type { Usuario } from '../api/tipos'
import { PANEL_DEL_EQUIPO, textosDeVisitaTerminada } from '../api/visita'
import { Aviso, Boton, Campo } from './ui'

/**
 * «Tu sesión ha caducado», encima de la pantalla y sin desmontarla: se vuelve a entrar aquí
 * mismo y lo que había fallado (un «Guardar», una subida) se repite solo. No se cierra
 * pulsando fuera ni con Escape: cerrarlo sin entrar dejaría la pantalla sin sesión y el
 * siguiente clic volvería a fallar. Las dos salidas son explícitas: entrar, o salir.
 *
 * «¿Has olvidado tu contraseña?» se abre en otra pestaña, para no perder esta; y quien ya ha
 * vuelto a entrar desde allí puede seguir sin escribir nada.
 */
export const SesionCaducada = ({ email, alEntrar, alSalir }: { alEntrar: (usuario: Usuario) => void; alSalir: () => void; email: string }) => {
  const [password, setPassword] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<unknown>(null)
  const [aviso, setAviso] = useState<null | string>(null)

  const entrar = async (e: FormEvent) => {
    e.preventDefault()
    setEnviando(true)
    setFallo(null)
    setAviso(null)
    try {
      const r = await pedir<{ user: Usuario }>('/usuarios/login?depth=1', { metodo: 'POST', cuerpo: { email, password }, toleraAnonimo: true })
      alEntrar(r.user)
    } catch (error) {
      setFallo(error)
    } finally {
      setEnviando(false)
    }
  }

  const yaEntre = async () => {
    setAviso(null)
    try {
      const r = await pedir<{ user: null | Usuario }>('/usuarios/me?depth=1', { toleraAnonimo: true })
      if (r.user) alEntrar(r.user)
      else setAviso('Todavía no hay ninguna sesión abierta en este navegador. Escribe tu contraseña aquí.')
    } catch (error) {
      setFallo(error)
    }
  }

  const mensaje = mensajeDeEntrada(fallo)

  return createPortal(
    <div aria-labelledby="sesion-caducada-titulo" aria-modal className="fixed inset-0 z-[60] flex items-center justify-center bg-velo/50 p-4 backdrop-blur-[2px]" role="dialog">
      <form className="w-full max-w-md rounded-caja border border-linea bg-papel p-6 shadow-alta" onSubmit={entrar}>
        <h2 className="font-display text-lg font-semibold" id="sesion-caducada-titulo">
          Tu sesión ha caducado
        </h2>
        <p className="mt-2 text-sm text-tenue">Por seguridad, la sesión se cierra si pasa un rato sin renovarse. Vuelve a entrar y seguimos donde lo dejaste: lo que tenías en pantalla sigue aquí.</p>
        <div className="mt-5 flex flex-col gap-4">
          <Campo autoComplete="username" disabled etiqueta="Correo" readOnly type="email" value={email} />
          <Campo autoComplete="current-password" autoFocus etiqueta="Contraseña" onChange={(e) => setPassword(e.target.value)} required type="password" value={password} />
          {mensaje && <Aviso tono="error">{mensaje}</Aviso>}
          {aviso && <Aviso tono="info">{aviso}</Aviso>}
          <Boton cargando={enviando} className="w-full" icono={<LogIn />} tipo="primario" type="submit">
            Entrar y seguir
          </Boton>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-[13px]">
          <a className="text-violeta hover:underline" href="/olvide" rel="noopener" target="_blank">
            ¿Has olvidado tu contraseña?
          </a>
          <button className="text-tenue hover:text-tinta" onClick={() => void yaEntre()} type="button">
            Ya he entrado en otra pestaña
          </button>
        </div>
        <div className="mt-5 border-t border-linea pt-4">
          <Boton onClick={alSalir} pequeno tipo="discreto">
            Salir sin guardar
          </Boton>
          <p className="mt-1 px-3 text-xs text-tenue">Lo que no hayas guardado se perderá.</p>
        </div>
      </form>
    </div>,
    document.body,
  )
}

/**
 * Cuando lo que caduca es **una visita del equipo** a la cuenta de un cliente (CLAUDE.md §4): la
 * sesión era la del propietario, pero quien estaba delante es del equipo, que no tiene su
 * contraseña ni debe tenerla. No se pide: se dice que la visita ha terminado (duran una hora y
 * no se alargan) y se ofrece volver al panel del equipo o entrar con la cuenta propia. Si el
 * equipo vuelve a entrar en la cuenta desde otra pestaña, «Ya he vuelto a entrar» sigue aquí
 * mismo y repite lo que había fallado, como con cualquier sesión.
 */
export const VisitaTerminada = ({ nombre, alEntrar, alSalir }: { alEntrar: (usuario: Usuario) => void; alSalir: () => void; nombre: string }) => {
  const [fallo, setFallo] = useState<unknown>(null)
  const [aviso, setAviso] = useState<null | string>(null)
  const textos = textosDeVisitaTerminada({ nombre })

  const yaEntre = async () => {
    setAviso(null)
    setFallo(null)
    try {
      const r = await pedir<{ user: null | Usuario }>('/usuarios/me?depth=1', { toleraAnonimo: true })
      if (r.user) alEntrar(r.user)
      else setAviso('Todavía no hay ninguna sesión abierta en este navegador. Entra en la cuenta desde el panel del equipo, en otra pestaña, y vuelve a pulsar aquí.')
    } catch (error) {
      setFallo(error)
    }
  }

  const mensaje = mensajeDeEntrada(fallo)

  return createPortal(
    <div aria-labelledby="visita-terminada-titulo" aria-modal className="fixed inset-0 z-[60] flex items-center justify-center bg-velo/50 p-4 backdrop-blur-[2px]" role="dialog">
      <div className="w-full max-w-md rounded-caja border border-linea bg-papel p-6 shadow-alta">
        <h2 className="font-display text-lg font-semibold" id="visita-terminada-titulo">
          {textos.titulo}
        </h2>
        <p className="mt-2 text-sm text-tenue">{textos.explicacion}</p>
        <div className="mt-5 flex flex-col gap-3">
          <Boton autoFocus className="w-full" icono={<ArrowLeft />} onClick={() => window.location.assign(PANEL_DEL_EQUIPO)} tipo="primario">
            Volver al panel del equipo
          </Boton>
          <Boton className="w-full" icono={<LogIn />} onClick={alSalir}>
            Entrar con mi cuenta
          </Boton>
          {mensaje && <Aviso tono="error">{mensaje}</Aviso>}
          {aviso && <Aviso tono="info">{aviso}</Aviso>}
        </div>
        <div className="mt-5 border-t border-linea pt-4">
          <p className="text-xs text-tenue">{textos.guardar}</p>
          <button className="mt-2 text-[13px] text-violeta hover:underline" onClick={() => void yaEntre()} type="button">
            Ya he vuelto a entrar en otra pestaña
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
